import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout/AdminLayout';
import AddBrandModal from '../../components/AddBrandModal/AddBrandModal';
import { brandsApi } from '../../services/apiService';
import { SOCKET_URL as API_BASE } from '../../config/api';
import Pagination from '../../components/Pagination/Pagination';
import './AddBrandPage.css';

const AddBrandPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const brandsPerPage = 18; // 6 columns x 3 rows

  // Fetch brands on component mount
  useEffect(() => {
    fetchBrands();
  }, []);

  const fetchBrands = async () => {
    try {
      setLoading(true);
      const data = await brandsApi.getAll();
      setBrands(data);
    } catch (err) {
      console.error('Error fetching brands:', err);
      setError('Failed to load brands');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBrand = async (brand) => {
    if (!window.confirm(`Are you sure you want to delete "${brand.name}"?`)) {
      return;
    }

    try {
      await brandsApi.delete(brand.id);
      setSuccess('Brand deleted successfully!');
      fetchBrands();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error deleting brand:', err);
      setError(err.response?.data?.error || 'Failed to delete brand');
    }
  };

  const handleToggleVisibility = async (brand) => {
    try {
      const formData = new FormData();
      formData.append('status', brand.status === 'Active' ? 'Inactive' : 'Active');
      await brandsApi.update(brand.id, formData);
      setSuccess(`Brand ${brand.status === 'Active' ? 'hidden' : 'visible'} successfully!`);
      fetchBrands();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error updating brand visibility:', err);
      setError('Failed to update brand visibility');
    }
  };

  const handleSaveBrand = async (brandData) => {
    // Validate brand name
    if (!brandData.name || !brandData.name.trim()) {
      throw new Error('Brand name is required');
    }

    const formData = new FormData();
    formData.append('name', brandData.name.trim());
    formData.append('status', 'Active');
    if (brandData.logo) {
      formData.append('logo', brandData.logo);
    }
    
    const result = await brandsApi.create(formData);
    console.log('Brand created successfully:', result);
    // Close modal on success
    setShowAddModal(false);
    setSuccess('Brand added successfully!');
    setError('');
    fetchBrands();
    setTimeout(() => setSuccess(''), 3000);
  };

  const filteredBrands = brands.filter(brand =>
    brand.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Pagination
  const totalPages = Math.ceil(filteredBrands.length / brandsPerPage);
  const indexOfLastBrand = currentPage * brandsPerPage;
  const indexOfFirstBrand = indexOfLastBrand - brandsPerPage;
  const currentBrands = filteredBrands.slice(indexOfFirstBrand, indexOfLastBrand);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  return (
    <AdminLayout>
      <div className="add-brand-content">
        {/* Page Header */}
        <div className="page-header-section">
          <div className="page-title-wrapper">
            <h1 className="page-title">Add Brand</h1>
          </div>
          <button className="add-brand-btn" onClick={() => setShowAddModal(true)}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 5V19" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M5 12H19" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Add New Brand</span>
          </button>
        </div>

        {error && (
          <div className="ab-alert ab-alert-error">
            <span>{error}</span>
            <button onClick={() => setError('')}>&times;</button>
          </div>
        )}

        {success && (
          <div className="ab-alert ab-alert-success">
            <span>{success}</span>
            <button onClick={() => setSuccess('')}>&times;</button>
          </div>
        )}

        {/* Brand Lists Section */}
        <div className="brand-lists-section">
          <div className="brand-lists-header">
            <h2 className="brand-lists-title">Brand lists</h2>
            <div className="search-box">
              <input 
                type="text" 
                className="search-input"
                placeholder="Search" 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <svg className="search-icon" width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M14.166 14.168L17.4993 17.5013" stroke="#969696" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M15.8333 9.16667C15.8333 5.48477 12.8486 2.5 9.16667 2.5C5.48477 2.5 2.5 5.48477 2.5 9.16667C2.5 12.8486 5.48477 15.8333 9.16667 15.8333C12.8486 15.8333 15.8333 12.8486 15.8333 9.16667Z" stroke="#969696" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>

          {loading ? (
            <div className="ab-loading">Loading brands...</div>
          ) : filteredBrands.length === 0 ? (
            <div className="ab-no-data">
              {searchQuery ? 'No brands found matching your search' : 'No brands yet. Add your first brand!'}
            </div>
          ) : (
            <>
              {/* Brand Grid - 6 columns */}
              <div className="brand-grid">
                {currentBrands.map((brand) => (
                  <div key={brand.id} className="brand-card">
                    <div className="brand-card-header">
                      <div className="brand-image-container">
                        {brand.logo ? (
                          <img 
                            src={`${API_BASE}${brand.logo}`} 
                            alt={brand.name}
                            className="brand-logo-img"
                            onError={(e) => {
                              e.target.onerror = null;
                              e.target.style.display = 'none';
                              e.target.parentElement.innerHTML = `<div class="brand-placeholder-circle"><span>${brand.name.charAt(0).toUpperCase()}</span></div>`;
                            }}
                          />
                        ) : (
                          <div className="brand-placeholder-circle">
                            <span>{brand.name.charAt(0).toUpperCase()}</span>
                          </div>
                        )}
                      </div>
                      <div className="brand-card-actions">
                        <button 
                          className="hide-btn"
                          onClick={() => handleToggleVisibility(brand)}
                          title={brand.status === 'Active' ? 'Hide brand' : 'Show brand'}
                        >
                          {brand.status === 'Active' ? (
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M2 12C2 12 5 5 12 5C19 5 22 12 22 12C22 12 19 19 12 19C5 19 2 12 2 12Z" stroke="#6D6D6D" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="#6D6D6D" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          ) : (
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <path d="M11.0485 9.21876C11.3643 9.18219 11.682 9.16392 12 9.16406C15.4944 9.16406 18.2934 11.339 19.4922 14.4086C19.202 15.1552 18.8099 15.8582 18.3271 16.4974M7.89428 10.3021C6.36587 11.2349 5.18211 12.6801 4.50781 14.4086C5.70656 17.4781 8.50564 19.6531 12 19.6531C13.4476 19.6608 14.8689 19.2665 16.1057 18.5143M10.4117 12.8203C10.2029 13.029 10.0373 13.2768 9.92438 13.5495C9.81142 13.8222 9.75328 14.1145 9.75328 14.4097C9.75328 14.7049 9.81142 14.9972 9.92438 15.2699C10.0373 15.5427 10.2029 15.7905 10.4117 15.9992C10.6204 16.2079 10.8682 16.3735 11.1409 16.4865C11.4136 16.5994 11.7059 16.6576 12.0011 16.6576C12.2963 16.6576 12.5886 16.5994 12.8613 16.4865C13.1341 16.3735 13.3819 16.2079 13.5906 15.9992" stroke="#6D6D6D" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M6.00781 8.41406L17.9953 20.4016" stroke="#6D6D6D" strokeLinecap="round"/>
                            </svg>
                          )}
                        </button>
                        <button 
                          className="delete-btn"
                          onClick={() => handleDeleteBrand(brand)}
                          title="Delete brand"
                        >
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M17.5 8H15V7.5C15 7.10218 14.842 6.72064 14.5607 6.43934C14.2794 6.15804 13.8978 6 13.5 6H10.5C10.1022 6 9.72064 6.15804 9.43934 6.43934C9.15804 6.72064 9 7.10218 9 7.5V8H6.5C6.36739 8 6.24021 8.05268 6.14645 8.14645C6.05268 8.24021 6 8.36739 6 8.5C6 8.63261 6.05268 8.75979 6.14645 8.85355C6.24021 8.94732 6.36739 9 6.5 9H7V18C7 18.2652 7.10536 18.5196 7.29289 18.7071C7.48043 18.8946 7.73478 19 8 19H16C16.2652 19 16.5196 18.8946 16.7071 18.7071C16.8946 18.5196 17 18.2652 17 18V9H17.5C17.6326 9 17.7598 8.94732 17.8536 8.85355C17.9473 8.75979 18 8.63261 18 8.5C18 8.36739 17.9473 8.24021 17.8536 8.14645C17.7598 8.05268 17.6326 8 17.5 8ZM10 7.5C10 7.36739 10.0527 7.24021 10.1464 7.14645C10.2402 7.05268 10.3674 7 10.5 7H13.5C13.6326 7 13.7598 7.05268 13.8536 7.14645C13.9473 7.24021 14 7.36739 14 7.5V8H10V7.5ZM16 18H8V9H16V18ZM11 11.5V15.5C11 15.6326 10.9473 15.7598 10.8536 15.8536C10.7598 15.9473 10.6326 16 10.5 16C10.3674 16 10.2402 15.9473 10.1464 15.8536C10.0527 15.7598 10 15.6326 10 15.5V11.5C10 11.3674 10.0527 11.2402 10.1464 11.1464C10.2402 11.0527 10.3674 11 10.5 11C10.6326 11 10.7598 11.0527 10.8536 11.1464C10.9473 11.2402 11 11.3674 11 11.5ZM14 11.5V15.5C14 15.6326 13.9473 15.7598 13.8536 15.8536C13.7598 15.9473 13.6326 16 13.5 16C13.3674 16 13.2402 15.9473 13.1464 15.8536C13.0527 15.7598 13 15.6326 13 15.5V11.5C13 11.3674 13.0527 11.2402 13.1464 11.1464C13.2402 11.0527 13.3674 11 13.5 11C13.6326 11 13.7598 11.0527 13.8536 11.1464C13.9473 11.2402 14 11.3674 14 11.5Z" fill="#DC3545"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                    <span className="brand-name">{brand.name}</span>
                  </div>
                ))}
              </div>

              {/* Pagination */}
              <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
            </>
          )}
        </div>
      </div>

      {/* Add Brand Modal */}
      <AddBrandModal 
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSave={handleSaveBrand}
      />
    </AdminLayout>
  );
};

export default AddBrandPage;
